import {
  useQueries,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import { useCallback } from 'react'
import { useWorkspace } from '@opallabs/hooks'
import {
  useUpdateOrCreatePendingBulkRequest,
  usePendingBulkRequests,
  useDeletePendingBulkRequest,
  invalidatePendingBulkRequestRecords,
  type PendingBulkRequest,
  type PendingBulkRequestViewContext,
} from '@opallabs/indexed-db'
import { toast, Toast } from '@opallabs/lapidary'
import { OpalApiRequestParams } from '../../schema'
import { bulkOperation } from '../../v3/bulkUpdate'
import { globalQueryKeys } from '../queryKeys'
import type {
  OnBulkPollingSuccess,
  BulkOperationResponse,
  OptionalRecord,
} from './types'

const initialProgress = 0.1

type UseBulkOperationPollingArgs<TOperationData extends OptionalRecord> = {
  onSuccess: OnBulkPollingSuccess<TOperationData>
  query?: OpalApiRequestParams<'ReadBulkOperationV3'>['query']
  /**
   * Unique identifier for the view and UI related to this request
   */
  requestViewContext: PendingBulkRequestViewContext
}

// Operations already handled. A refetch (window focus, a second mounted
// instance) can land before the pending record is deleted, and must not run
// `onSuccess` twice.
const completedOperations = new Set<number>()
// Track which operations have visible toasts.
// toastId === operationId
const toastsShown = new Set<string>()

const isComplete = (response?: BulkOperationResponse) =>
  response?.data.attributes.status === 'complete'

const updateToast = (
  operationId: string,
  included: BulkOperationResponse['included'] = []
) => {
  if (!toastsShown.has(operationId)) {
    toast(
      (props) => (
        <Toast
          {...props}
          title="Bulk Update In Progress"
          message="Your update is in progress and we'll let you know as soon as it finishes"
          showProgressWheel={true}
        />
      ),
      {
        toastId: operationId,
        type: 'default',
        progress: initialProgress,
      }
    )

    return toastsShown.add(operationId)
  }

  const total = included.length
  if (total === 0) return

  const completed = included.filter(
    ({ attributes }) => attributes.operation_status !== 'pending'
  ).length
  const progress = (completed / total) * (1 - initialProgress)

  toast.update(operationId, { progress: initialProgress + progress })
}

export const useBulkOperationPolling = <
  TOperationData extends OptionalRecord = undefined,
>({
  onSuccess,
  query,
  requestViewContext,
}: UseBulkOperationPollingArgs<TOperationData>) => {
  const queryClient = useQueryClient()
  const { uuid: workspaceUuid } = useWorkspace()

  const { data: pendingOperations } = usePendingBulkRequests()
  const updateOrCreateOperation = useUpdateOrCreatePendingBulkRequest({
    onSuccess: () => {
      invalidatePendingBulkRequestRecords(queryClient)
    },
  })
  const deletePendingOperation = useDeletePendingBulkRequest({
    onSuccess: async (id) => {
      await invalidatePendingBulkRequestRecords(queryClient)
      completedOperations.delete(id)
    },
  })

  const setPendingOperation = useCallback(
    (operation: Omit<PendingBulkRequest, 'id'>) => {
      updateOrCreateOperation(operation)
    },
    [updateOrCreateOperation]
  )

  const removePendingOperation = useCallback(
    ({ id }: Pick<PendingBulkRequest, 'id'>) => {
      completedOperations.add(id)
      deletePendingOperation.mutate({ id })
    },
    [deletePendingOperation]
  )

  useQueries({
    // There may be several pending requests saved in indexed-db, from views
    // other than this one. Polling only this view's requests keeps another
    // mounted instance from consuming the record before this hook's `onSuccess`
    // and toast can run.
    queries: (pendingOperations ?? [])
      .filter(({ viewContext }) => viewContext === requestViewContext)
      .map(
        ({
          id,
          operationId,
          operationData,
        }): UseQueryOptions<BulkOperationResponse> => ({
          queryKey: globalQueryKeys.bulkOperation(
            workspaceUuid,
            operationId,
            query
          ),
          queryFn: () =>
            bulkOperation.read({
              path: { bulk_operation_id: operationId },
              query: query ?? {},
            }),
          refetchInterval: (response) => (isComplete(response) ? false : 5_000),
          onSuccess: (response) => {
            if (!isComplete(response))
              return updateToast(operationId, response.included)
            if (completedOperations.has(id)) return

            onSuccess(response, operationData as TOperationData)
            toast.done(operationId)
            toastsShown.delete(operationId)
            removePendingOperation({ id })
          },
        })
      ),
  })

  return setPendingOperation
}
